import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Download, Upload, Trash2, Pencil, Save, X, FileSpreadsheet, Loader2, Camera, AlertCircle, Settings, PackagePlus, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatMoney } from "@/components/vendors/utils";
import CatalogManagerDialog from "@/components/inventario/CatalogManagerDialog";
import ImportFromAlbaranDialog from "@/components/inventario/ImportFromAlbaranDialog";

import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
const CATEGORY_LABELS_ES = {
  food_cost: "Food Cost", groceries: "Alimentación", carnes: "Carnes",
  pescado_marisco: "Pescado y marisco", vinos: "Vinos", cerveza: "Cerveza",
  refrescos: "Refrescos", licores: "Licores", lacteos: "Lácteos",
  aceites: "Aceites", consumibles: "Consumibles", limpieza: "Limpieza",
  huevos: "Huevos", otros: "Otros",
};
const catLabel = (c) => CATEGORY_LABELS_ES[c] || (c || "otros");

function groupBy(items, keyFn) {
  const out = {};
  for (const it of items) {
    const k = keyFn(it) || "otros";
    if (!out[k]) out[k] = [];
    out[k].push(it);
  }
  return out;
}

/** Group items by category → subcategory (or "—" when null). */
function groupByCatSub(items) {
  const out = {};
  for (const it of items) {
    const cat = it.category || "otros";
    const sub = it.subcategory || "—";
    if (!out[cat]) out[cat] = {};
    if (!out[cat][sub]) out[cat][sub] = [];
    out[cat][sub].push(it);
  }
  return out;
}

export default function Inventario() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const baseCurrency = user?.base_currency || "EUR";
  const [list, setList] = useState([]);
  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // inventory doc or "new"
  const uploadInput = useRef(null);
  const photoInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [ocring, setOcring] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // iter 82.7p — after `/inventories/from-photo` returns we open this
  // preview modal so the user confirms each row and the match against
  // their /productos catalog before persisting.
  const [photoPreview, setPhotoPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventories");
      setList(data.inventories || []);
      setReminder(data.reminder || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    try {
      const { data } = await api.get("/inventories/catalog");
      const products = data.products || [];
      const items = products.map((p) => ({
        product_id: null,
        name: p.name,
        category: p.category,
        subcategory: p.subcategory,
        unit: p.unit,
        quantity: 0,
        unit_price: p.latest_unit_price || 0,
      }));
      setEditing({
        id: null,
        date: new Date().toISOString().slice(0, 10),
        name: "",
        notes: "",
        items,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /** Re-fetch catalog and merge into the open editor: preserves user-entered
   * quantities for products still present, drops hidden ones, appends new. */
  const refreshEditorCatalog = async () => {
    if (!editing) return;
    try {
      const { data } = await api.get("/inventories/catalog");
      const products = data.products || [];
      const qtyByKey = Object.fromEntries(
        (editing.items || []).map((it) => [(it.name || "").toLowerCase(), it])
      );
      const items = products.map((p) => {
        const prev = qtyByKey[(p.name || "").toLowerCase()];
        return {
          product_id: null,
          name: p.name,
          category: p.category,
          subcategory: p.subcategory,
          unit: p.unit,
          quantity: prev ? prev.quantity : 0,
          unit_price: prev ? prev.unit_price : (p.latest_unit_price || 0),
        };
      });
      setEditing((cur) => cur ? { ...cur, items } : cur);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = async (invId) => {
    try {
      const { data } = await api.get(`/inventories/${invId}`);
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (invId) => {
    if (!window.confirm(lang === "es" ? "¿Borrar este inventario?" : "Delete this inventory?")) return;
    try {
      await api.delete(`/inventories/${invId}`);
      toast.success(lang === "es" ? "Borrado" : "Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/inventories/template/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `plantilla-inventario.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const date = new Date().toISOString().slice(0, 10);
      await api.post(`/inventories/from-template?date=${date}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(lang === "es" ? "Inventario importado" : "Inventory imported");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (uploadInput.current) uploadInput.current.value = ""; }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setOcring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const date = new Date().toISOString().slice(0, 10);
      const [{ data }, catRes] = await Promise.all([
        api.post(`/inventories/from-photo?date=${date}`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
        api.get("/inventories/catalog").catch(() => ({ data: { products: [] } })),
      ]);
      const catalog = catRes.data.products || [];
      // Build preview rows: OCR extraction on the left, best-guess catalog
      // link on the right. Unmatched rows point to "" (user has to link or
      // choose "New").
      const rows = [
        ...(data.matched || []).map((m) => ({
          _uid: crypto.randomUUID(),
          ocr_name: m.name,
          quantity: Number(m.quantity ?? 0),
          unit: m.unit || "",
          suggested_catalog_name: m.name,
          catalog_name: m.name,           // user can change via dropdown
          category: m.category,
          subcategory: m.subcategory,
          unit_price: Number(m.unit_price ?? 0),
          include: true,
          status: "matched",
        })),
        ...(data.unmatched || []).map((u) => ({
          _uid: crypto.randomUUID(),
          ocr_name: u.name,
          quantity: Number(u.quantity ?? 0),
          unit: u.unit || "",
          suggested_catalog_name: null,
          catalog_name: "",               // needs user action
          category: "otros",
          subcategory: null,
          unit_price: 0,
          include: true,
          status: "unmatched",
        })),
      ];
      if (rows.length === 0) {
        toast.warning(lang === "es" ? "No se pudo extraer ningún producto de la foto" : "No products extracted");
        return;
      }
      setPhotoPreview({ date: data.date, catalog, rows });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcring(false); if (photoInput.current) photoInput.current.value = ""; }
  };

  // iter 82.7p — user confirmed the preview → build inventory items and
  // hand off to the regular editor for a final review + save.
  const confirmPhotoPreview = () => {
    if (!photoPreview) return;
    const catByName = new Map(
      (photoPreview.catalog || []).map((p) => [p.name.trim().toLowerCase(), p]),
    );
    const items = photoPreview.rows
      .filter((r) => r.include)
      .map((r) => {
        const link = (r.catalog_name || "").trim().toLowerCase()
          ? catByName.get(r.catalog_name.trim().toLowerCase())
          : null;
        return {
          product_id: null,
          name: link ? link.name : r.ocr_name,
          category: link?.category || r.category || "otros",
          subcategory: link?.subcategory || r.subcategory || null,
          unit: link?.unit || r.unit || "",
          quantity: Number(r.quantity) || 0,
          unit_price: Number(link?.latest_unit_price ?? r.unit_price ?? 0) || 0,
        };
      });
    setEditing({
      id: null,
      date: photoPreview.date,
      name: lang === "es" ? "Desde foto" : "From photo",
      notes: "",
      items,
    });
    setPhotoPreview(null);
  };

  const exportXlsx = async (invId, date) => {
    try {
      const res = await api.get(`/inventories/${invId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `inventario-${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) {
    return <InventoryEditor
      inv={editing}
      lang={lang}
      baseCurrency={baseCurrency}
      onClose={() => setEditing(null)}
      onSaved={() => { setEditing(null); load(); }}
    />;
  }

  return (
    <div className="space-y-4" data-testid="inventario-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">
            {lang === "es" ? "Inventario" : "Inventory"}
          </h1>
          <p className="text-xs text-[var(--text-muted)]">
            {lang === "es"
              ? "Recuentos mensuales de productos Food Cost y Limpieza. Valorados a precio unitario más reciente."
              : "Monthly counts of Food Cost and Cleaning products, valued at the latest unit price."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setCatalogOpen(true)} data-testid="inv-catalog-btn">
            <Settings size={14} className="mr-1.5" />
            {lang === "es" ? "Catálogo" : "Catalog"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="inv-import-albaran-btn">
            <PackagePlus size={14} className="mr-1.5" />
            {lang === "es" ? "Desde albarán" : "From delivery note"}
          </Button>
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="inv-download-template-btn">
            <Download size={14} className="mr-1.5" />
            {lang === "es" ? "Plantilla" : "Template"}
          </Button>
          <input ref={uploadInput} type="file" accept=".xlsx" className="hidden"
            onChange={(e) => uploadTemplate(e.target.files?.[0])} />
          <Button size="sm" variant="outline" onClick={() => uploadInput.current?.click()} disabled={uploading} data-testid="inv-upload-template-btn">
            {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
            {lang === "es" ? "Subir plantilla" : "Upload"}
          </Button>
          <input ref={photoInput} type="file" accept="image/*,application/pdf" className="hidden"
            onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => photoInput.current?.click()}
            disabled={ocring}
            data-testid="inv-photo-btn"
            title={lang === "es" ? "Sube una foto/PDF del recuento manuscrito — la IA rellena las cantidades" : "Upload a photo/PDF — AI will fill quantities"}
            className="border-[var(--brand-accent)]/50 text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/10"
          >
            {ocring ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Camera size={14} className="mr-1.5" />}
            {lang === "es" ? "Foto (IA)" : "Photo (AI)"}
          </Button>
          <Button size="sm" onClick={openNew} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="inv-new-btn">
            <Plus size={14} className="mr-1.5" />
            {lang === "es" ? "Nuevo inventario" : "New inventory"}
          </Button>
        </div>
      </div>

      {reminder?.should_show && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-3" data-testid="inv-reminder-banner">
          <div className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400">
            <AlertCircle size={18} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              {lang === "es"
                ? (reminder.stage === "eom"
                    ? `Aún no has hecho el inventario de ${reminder.target_ym}`
                    : `Todavía no has cerrado el inventario de ${reminder.target_ym}`)
                : (reminder.stage === "eom"
                    ? `No inventory saved for ${reminder.target_ym} yet`
                    : `You haven't closed ${reminder.target_ym}'s inventory yet`)}
            </p>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
              {lang === "es"
                ? "Haz el recuento con la plantilla, o sube una foto del recuento manuscrito y deja que la IA rellene las cantidades."
                : "Fill the template, or upload a photo of your handwritten count and let AI fill quantities."}
            </p>
          </div>
        </div>
      )}

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--surface-hover)]">
            <tr>
              <th className="text-left py-2.5 px-3 font-medium">{lang === "es" ? "Fecha" : "Date"}</th>
              <th className="text-left py-2.5 px-3 font-medium">{lang === "es" ? "Nombre" : "Name"}</th>
              <th className="text-right py-2.5 px-3 font-medium">{lang === "es" ? "Nº líneas" : "Lines"}</th>
              <th className="text-right py-2.5 px-3 font-medium">{lang === "es" ? "Valor" : "Value"}</th>
              <th className="text-right py-2.5 px-3 font-medium">{lang === "es" ? "Acciones" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} className="text-center py-8 text-[var(--text-muted)]">…</td></tr>)}
            {!loading && list.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-[var(--text-muted)]">
                {lang === "es" ? "Aún no tienes inventarios. Crea el primero o sube una plantilla rellenada." : "No inventories yet. Create the first or upload a filled template."}
              </td></tr>
            )}
            {list.map((inv) => (
              <tr key={inv.id} className="border-t border-[var(--border-color)] hover:bg-[var(--surface-hover)]" data-testid={`inv-row-${inv.id}`}>
                <td className="py-2.5 px-3 font-mono-num text-xs">{inv.date}</td>
                <td className="py-2.5 px-3">{inv.name || "—"}</td>
                <td className="py-2.5 px-3 text-right font-mono-num text-xs">{(inv.items || []).length}</td>
                <td className="py-2.5 px-3 text-right font-mono-num font-semibold">{formatMoney(inv.total, inv.currency || baseCurrency)}</td>
                <td className="py-2.5 px-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => exportXlsx(inv.id, inv.date)} title="Excel" data-testid={`inv-export-${inv.id}`}>
                      <FileSpreadsheet size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(inv.id)} data-testid={`inv-edit-${inv.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(inv.id)} className="text-[var(--status-negative)]" data-testid={`inv-delete-${inv.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CatalogManagerDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        lang={lang}
        onChanged={() => { if (editing) refreshEditorCatalog(); }}
      />
      <ImportFromAlbaranDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        lang={lang}
        onImported={() => { if (editing) refreshEditorCatalog(); }}
      />
      {photoPreview && (
        <PhotoPreviewDialog
          preview={photoPreview}
          onChange={setPhotoPreview}
          onClose={() => setPhotoPreview(null)}
          onConfirm={confirmPhotoPreview}
          lang={lang}
        />
      )}
    </div>
  );
}


// ==================================================================
// iter 82.7p — Photo OCR preview modal. Shows each extracted row with
// an editable qty + catalog link so the user can confirm before the
// items land in the inventory editor.
// ==================================================================
function PhotoPreviewDialog({ preview, onChange, onClose, onConfirm, lang }) {
  const catalog = preview.catalog || [];
  const updateRow = (uid, patch) =>
    onChange({ ...preview, rows: preview.rows.map((r) => (r._uid === uid ? { ...r, ...patch } : r)) });
  const includedCount = preview.rows.filter((r) => r.include).length;
  const matchedCount = preview.rows.filter((r) => r.include && r.status === "matched" && r.catalog_name).length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="photo-preview-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Revisa el inventario extraído" : "Review extracted inventory"}</DialogTitle>
          <DialogDescription>
            {lang === "es"
              ? `La IA leyó ${preview.rows.length} líneas de la foto. Vincúlalas con tu catálogo de productos, corrige las cantidades y desmarca las que quieras excluir. Al confirmar, se abrirá el editor de inventario con estas líneas ya cargadas.`
              : `AI read ${preview.rows.length} lines from the photo. Link them to your product catalog, tweak quantities and untick anything you want to drop. Confirming loads them into the inventory editor.`}
          </DialogDescription>
        </DialogHeader>
        <div className="text-[11px] flex items-center gap-3 text-[var(--text-muted)] mt-1">
          <span>{lang === "es" ? "Fecha" : "Date"}: <b className="font-mono-num">{preview.date}</b></span>
          <span>·</span>
          <span>{lang === "es" ? "Incluidos" : "Included"}: <b>{includedCount}</b>/{preview.rows.length}</span>
          <span>·</span>
          <span>{lang === "es" ? "Emparejados" : "Matched"}: <b>{matchedCount}</b></span>
        </div>
        <div className="overflow-x-auto border border-[var(--border-color)] rounded-md mt-3">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-hover)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              <tr>
                <th className="text-left py-1.5 px-2 w-8"></th>
                <th className="text-left py-1.5 px-2">{lang === "es" ? "Texto detectado" : "Detected"}</th>
                <th className="text-left py-1.5 px-2">{lang === "es" ? "Vincular con producto" : "Link to product"}</th>
                <th className="text-right py-1.5 px-2 w-20">{lang === "es" ? "Cant." : "Qty"}</th>
                <th className="text-left py-1.5 px-2 w-20">Ud.</th>
                <th className="text-center py-1.5 px-2 w-24">Estado</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr
                  key={r._uid}
                  className={`border-t border-[var(--border-color)] ${!r.include ? "opacity-40" : ""}`}
                  data-testid={`photo-preview-row-${r._uid}`}
                >
                  <td className="py-1.5 px-2">
                    <input
                      type="checkbox"
                      checked={r.include}
                      onChange={(e) => updateRow(r._uid, { include: e.target.checked })}
                      data-testid={`photo-preview-include-${r._uid}`}
                    />
                  </td>
                  <td className="py-1.5 px-2 text-xs">{r.ocr_name}</td>
                  <td className="py-1.5 px-2">
                    <select
                      value={r.catalog_name}
                      onChange={(e) => updateRow(r._uid, { catalog_name: e.target.value })}
                      className="h-8 w-full text-xs rounded border border-[var(--border-color)] bg-[var(--surface)] px-1.5"
                      data-testid={`photo-preview-catalog-${r._uid}`}
                    >
                      <option value="">
                        {lang === "es" ? "— Producto nuevo —" : "— New product —"}
                      </option>
                      {catalog.map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 px-2">
                    <input
                      type="number"
                      step="0.01"
                      value={r.quantity}
                      onChange={(e) => updateRow(r._uid, { quantity: e.target.value })}
                      className="h-8 w-20 text-right text-xs rounded border border-[var(--border-color)] bg-[var(--surface)] px-1.5 font-mono-num"
                      data-testid={`photo-preview-qty-${r._uid}`}
                    />
                  </td>
                  <td className="py-1.5 px-2">
                    <input
                      value={r.unit}
                      onChange={(e) => updateRow(r._uid, { unit: e.target.value })}
                      className="h-8 w-16 text-xs rounded border border-[var(--border-color)] bg-[var(--surface)] px-1.5"
                    />
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      r.catalog_name
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    }`}>
                      {r.catalog_name
                        ? (lang === "es" ? "Vinculado" : "Linked")
                        : (lang === "es" ? "Nuevo" : "New")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} data-testid="photo-preview-cancel">
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={includedCount === 0}
            data-testid="photo-preview-confirm"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {lang === "es" ? "Confirmar y editar" : "Confirm & edit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InventoryEditor({ inv, lang, baseCurrency, onClose, onSaved }) {
  const [date, setDate] = useState(inv.date || new Date().toISOString().slice(0, 10));
  const [name, setName] = useState(inv.name || "");
  const [notes, setNotes] = useState(inv.notes || "");
  const [items, setItems] = useState(inv.items || []);
  const [saving, setSaving] = useState(false);

  const total = useMemo(
    () => items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0),
    [items],
  );

  const grouped = useMemo(() => groupByCatSub(items), [items]);

  const setItem = (idx, key, val) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));

  const hideFromCatalog = async (it) => {
    if (!it.name) return;
    if (!window.confirm(lang === "es"
      ? `¿Ocultar "${it.name}" del catálogo? Ya no aparecerá en futuros inventarios.`
      : `Hide "${it.name}" from catalog? It won't appear in future inventories.`)) return;
    try {
      await api.post("/inventories/catalog/hide", {
        name: it.name, category: it.category, subcategory: it.subcategory, unit: it.unit, hidden: true,
      });
      toast.success(lang === "es" ? "Ocultado del catálogo" : "Hidden from catalog");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addLoose = () => {
    setItems((prev) => [...prev, {
      product_id: null,
      name: "",
      category: "otros",
      subcategory: null,
      unit: "ud",
      quantity: 0,
      unit_price: 0,
    }]);
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { date, name, notes, currency: baseCurrency, items: items.filter((i) => i.name && i.name.trim()) };
      if (inv.id) await api.put(`/inventories/${inv.id}`, payload);
      else await api.post("/inventories", payload);
      toast.success(lang === "es" ? "Inventario guardado" : "Inventory saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="inventario-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">
            {inv.id ? (lang === "es" ? "Editar inventario" : "Edit inventory") : (lang === "es" ? "Nuevo inventario" : "New inventory")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="inv-editor-cancel">
            <X size={14} className="mr-1" />
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="inv-editor-save">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
            {lang === "es" ? "Guardar" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Fecha" : "Date"}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="inv-editor-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Nombre" : "Name"}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={lang === "es" ? "Ej: Cierre febrero" : "e.g. February close"} data-testid="inv-editor-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Notas" : "Notes"}</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inv-editor-notes" />
        </div>
      </div>

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        <div className="p-3 border-b border-[var(--border-color)] flex items-center justify-between">
          <p className="text-xs text-[var(--text-muted)]">
            {items.length} {lang === "es" ? "productos" : "products"} · <span className="font-semibold text-[var(--text-primary)] font-mono-num">{formatMoney(total, baseCurrency)}</span>
          </p>
          <Button size="sm" variant="outline" onClick={addLoose} data-testid="inv-editor-add-loose">
            <Plus size={12} className="mr-1" />
            {lang === "es" ? "Producto suelto" : "Loose product"}
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {Object.entries(grouped).sort().map(([cat, subs]) => (
            <div key={cat}>
              <div className="px-3 py-2 bg-[var(--surface-hover)] border-t border-[var(--border-color)] text-[11px] uppercase tracking-wider text-[var(--text-primary)] font-bold">
                {catLabel(cat)}
              </div>
              {Object.entries(subs).sort().map(([sub, its]) => (
                <div key={sub}>
                  {sub !== "—" && (
                    <div className="px-6 py-1 bg-[var(--surface-hover)]/50 text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-medium border-t border-[var(--border-color)]/40">
                      {sub}
                    </div>
                  )}
                  {its.map((it) => {
                    const idx = items.indexOf(it);
                    const subtotal = Number(it.quantity || 0) * Number(it.unit_price || 0);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-t border-[var(--border-color)]/50 text-xs hover:bg-[var(--surface-hover)]" data-testid={`inv-row-${idx}`}>
                        <Input className="col-span-4 h-8 text-xs" value={it.name} onChange={(e) => setItem(idx, "name", e.target.value)} data-testid={`inv-item-name-${idx}`} />
                        <Input className="col-span-1 h-8 text-xs" value={it.unit || ""} onChange={(e) => setItem(idx, "unit", e.target.value)} placeholder="ud" />
                        <Input type="number" step="0.01" className="col-span-2 h-8 text-xs font-mono-num text-right" value={it.quantity ?? 0} onChange={(e) => setItem(idx, "quantity", parseFloat(e.target.value) || 0)} data-testid={`inv-item-qty-${idx}`} />
                        <Input type="number" step="0.0001" className="col-span-2 h-8 text-xs font-mono-num text-right" value={it.unit_price ?? 0} onChange={(e) => setItem(idx, "unit_price", parseFloat(e.target.value) || 0)} />
                        <div className="col-span-2 text-right font-mono-num font-semibold">{formatMoney(subtotal, baseCurrency)}</div>
                        <div className="col-span-1 flex items-center justify-end gap-1">
                          <button onClick={() => hideFromCatalog(it)} className="text-[var(--text-muted)] hover:text-amber-600" title={lang === "es" ? "Ocultar del catálogo" : "Hide from catalog"} data-testid={`inv-hide-${idx}`}>
                            <EyeOff size={12} />
                          </button>
                          <button onClick={() => removeItem(idx)} className="text-[var(--status-negative)] hover:text-red-700" title={lang === "es" ? "Quitar de este inventario" : "Remove from this inventory"}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
